import { useCallback, useEffect, useState } from 'react';
import { ResetJobData, ResetStatusData } from './useSubmissionTemplateVersion';

interface UseResetToProdButtonOptions {
  // False while a required id (e.g. a route param) hasn't resolved yet —
  // the hook skips polling/triggering entirely until this is true, so
  // per-template callers don't need their own "if (!id) return" guard.
  enabled?: boolean;
  getStatus: () => Promise<ResetStatusData>;
  trigger: () => Promise<{ last_job: ResetJobData | null }>;
  confirmMessage: string;
  startedMessage: string;
  failedMessage: string;
}

// Shared "poll status on mount, confirm, trigger, disable while in flight"
// state machine for a reset-to-prod button — used identically by the
// per-template button (Versions.tsx) and the "reset all" button
// (submission_templates/Index.tsx), which differ only in which
// getResetStatus/triggerReset vs. getResetAllStatus/triggerResetAll pair
// and copy strings they're wired to.
export const useResetToProdButton = ({
  enabled = true,
  getStatus,
  trigger,
  confirmMessage,
  startedMessage,
  failedMessage,
}: UseResetToProdButtonOptions) => {
  const [status, setStatus] = useState<ResetStatusData | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset-to-prod is entirely optional infrastructure — a non-prod
  // environment without TEMPLATE_SYNC_PROD_BASE_URL/API_KEY configured (or
  // production itself) just never shows the button, rather than showing a
  // broken one. Clearing `status` (rather than just skipping the fetch) when
  // `enabled` goes false prevents a stale status — e.g. from a previous id —
  // from keeping the button visibly enabled after the thing it targets is
  // no longer valid.
  useEffect(() => {
    if (!enabled) {
      setStatus(null);
      return;
    }
    getStatus()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [enabled, getStatus]);

  const isInFlight =
    isTriggering ||
    status?.last_job?.state === 'queued' ||
    status?.last_job?.state === 'running';

  const handleTrigger = useCallback(async () => {
    if (!enabled) return;
    if (!confirm(confirmMessage)) return;

    setIsTriggering(true);
    setError(null);
    try {
      const result = await trigger();
      setNotice(startedMessage);
      setStatus(prev => (prev ? { ...prev, last_job: result.last_job } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : failedMessage);
    } finally {
      setIsTriggering(false);
    }
  }, [enabled, confirmMessage, trigger, startedMessage, failedMessage]);

  return { status, isInFlight, notice, error, handleTrigger };
};
